import { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { supabase, funzioneUrl } from './supabase'

const AuthContext = createContext({})

export function AuthProvider({ children }) {
  const [utente, setUtente] = useState(null)
  const [profilo, setProfilo] = useState(null)
  const [caricamento, setCaricamento] = useState(true)
  const [errore, setErrore] = useState(null)

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUtente(session?.user ?? null)
      if (session?.user) caricaProfilo(session.user.id)
      else setCaricamento(false)
    })
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      // Se il token refresh fallisce, Supabase emette SIGNED_OUT — gestisci logout pulito
      if (_e === 'SIGNED_OUT' || _e === 'TOKEN_REFRESHED' && !session) {
        setUtente(null); setProfilo(null); setCaricamento(false); setErrore(null)
        return
      }
      setUtente(session?.user ?? null)
      if (session?.user) caricaProfilo(session.user.id)
      else { setProfilo(null); setCaricamento(false); setErrore(null) }
    })
    return () => subscription.unsubscribe()
  }, [])

  const caricaProfilo = useCallback(async (id, tentativo = 0) => {
    try {
      setErrore(null)
      const idPulito = String(id).trim()
      // maybeSingle() restituisce null invece di 406 se il profilo non esiste ancora
      const { data, error } = await supabase
        .from('profili')
        .select('id, email, nome, cognome, piano, abbonamento_attivo, abbonamento_dal, abbonamento_al, regalo, bloccato, ruolo_scelto')
        .eq('id', idPulito)
        .maybeSingle()

      if (error) {
        // Retry fino a 5 volte con attesa crescente (profilo potrebbe non essere ancora creato)
        if (tentativo < 5) {
          setTimeout(() => caricaProfilo(id, tentativo + 1), 1000 * (tentativo + 1))
          return
        }
        console.error('Errore caricamento profilo:', error)
        setErrore('Errore di connessione. Ricarica la pagina.')
        setProfilo(null)
      } else if (!data) {
        // Profilo non ancora creato (trigger Supabase in ritardo) — riprova
        if (tentativo < 5) {
          setTimeout(() => caricaProfilo(id, tentativo + 1), 1000 * (tentativo + 1))
          return
        }
        // Dopo 5 tentativi senza profilo, mostra errore
        setErrore('Errore di connessione. Ricarica la pagina.')
        setProfilo(null)
      } else {
        setProfilo(data)
      }
    } catch (e) {
      console.error('Errore profilo:', e)
      if (tentativo < 5) {
        setTimeout(() => caricaProfilo(id, tentativo + 1), 1000 * (tentativo + 1))
        return
      }
      setErrore('Errore di connessione. Ricarica la pagina.')
    } finally {
      setCaricamento(false)
    }
  }, [])

  // redirectTo: indirizzo a cui riportare l'utente dopo aver confermato l'email.
  // Conserva i parametri d'ingresso (invito al gruppo, referral, regalo), così
  // l'iscrizione si completa da sola al ritorno invece di perdersi per strada.
  async function registrati({ email, password, nome, cognome }, redirectTo) {
    const { data, error } = await supabase.auth.signUp({
      email, password,
      options: { data: { nome, cognome }, ...(redirectTo ? { emailRedirectTo: redirectTo } : {}) }
    })

    // L'email di benvenuto NON parte più da qui: con la conferma email attiva
    // la registrazione non produce una sessione, quindi la chiamata fallirebbe.
    // La manda completaIscrizione(), quando l'utente è davvero autenticato.

    return { data, error }
  }

  async function accedi({ email, password }) {
    return await supabase.auth.signInWithPassword({ email, password })
  }

  async function esci() {
    await supabase.auth.signOut()
    setProfilo(null)
    setErrore(null)
  }

  return (
    <AuthContext.Provider value={{ utente, profilo, caricamento, errore, registrati, accedi, esci, caricaProfilo }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
